import styled, { css } from 'styled-components';
import { softControlMotion } from '@/theme/interactions';
import type { ButtonHTMLAttributes, Ref } from 'react';

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
type Size = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  // React 19 passes `ref` through to function components as a normal prop
  // (no forwardRef needed) — declared explicitly so callers that need to
  // focus a Button imperatively (e.g. a confirm dialog's default action,
  // see SubmitFeedbackPanel's discard-confirm overlay) are type-checked.
  ref?: Ref<HTMLButtonElement>;
}

const variants = {
  /* The one accent fill on a view. No resting halo: elevation.glow is the FOCUS
     ring now, and a permanent coloured bloom under every primary button was the
     single most template-looking thing in the app. */
  primary: css`
    background: ${({ theme }) => theme.color.accentCyan};
    color: ${({ theme }) => theme.color.onAccent};
    border-color: transparent;
  `,
  secondary: css`
    background: ${({ theme }) => theme.color.surface};
    color: ${({ theme }) => theme.color.textPrimary};
    border-color: ${({ theme }) => theme.color.hairSoft};
  `,
  // Repeated/secondary actions (Follow, Join, the promo link). Deliberately NOT
  // accent-coloured: the accent is a mark on one control per view, and an
  // accent-outlined pill repeated down a suggestion list is what turned it into
  // a wash. Reads as a control through its border and weight, not its hue.
  outline: css`
    background: transparent;
    color: ${({ theme }) => theme.color.textPrimary};
    border-color: ${({ theme }) => theme.color.hairSoft};
  `,
  ghost: css`
    background: transparent;
    color: ${({ theme }) => theme.color.textPrimary};
    border-color: transparent;
  `,
  danger: css`
    background: ${({ theme }) => `color-mix(in srgb, ${theme.color.danger} 14%, transparent)`};
    color: ${({ theme }) => theme.color.danger};
    border-color: transparent;
  `,
} as const;

const StyledButton = styled.button<{ $variant: Variant; $size: Size }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: ${({ theme }) => theme.font.body};
  font-weight: 600;
  font-size: ${({ theme, $size }) => ($size === 'sm' ? theme.fontSize.xs : theme.fontSize.sm)};
  padding: ${({ $size }) => ($size === 'sm' ? '6px 12px' : '9px 16px')};
  border-radius: ${({ theme }) => theme.radius.md};
  border: 1px solid transparent;
  cursor: pointer;
  transition:
    background 120ms ease,
    color 120ms ease,
    border-color 120ms ease;
  ${({ $variant }) => variants[$variant]}
  ${softControlMotion}
  &:hover:not(:disabled):not([aria-disabled='true']),
  &[aria-pressed='true']:not(:disabled):not([aria-disabled='true']) {
    background-color: ${({ theme, $variant }) =>
      $variant === 'primary'
        ? `color-mix(in srgb, ${theme.color.accentCyan} 92%, ${theme.color.textPrimary})`
        : $variant === 'danger'
          ? `color-mix(in srgb, ${theme.color.danger} 22%, transparent)`
          : `color-mix(in srgb, ${theme.color.surface} 94%, ${theme.color.textPrimary})`};
  }

  /* Finish treatments (Appearance → Accent fill). Applied to the filled primary
     only. All three stay visibly different, so the setting is not inert:
     'flat' is the fill alone; 'metallic' adds a 1px top highlight (a struck
     edge, not the old 135deg two-stop accent gradient, which was a template CTA
     and the app's own playbook bans gradients on functional surfaces);
     'glow' keeps the halo, because that is a look a member opted into. */
  ${({ $variant, theme }) =>
    $variant === 'primary' &&
    theme.finish === 'metallic' &&
    css`
      box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.28);
    `}
  ${({ $variant, theme }) =>
    $variant === 'primary' &&
    theme.finish === 'glow' &&
    css`
      box-shadow: 0 0 18px ${theme.color.glow};
    `}
  /* Disabled: a defined muted-chip treatment (not a flat opacity dim, which
     washed the light-theme filled primary into an illegible pale-green ghost).
     Uniform across variants: muted surface + muted text + soft border, no glow.
     NO opacity: the muted colours alone signal "inert", and dimming would drop
     the label below AA contrast on the white light-theme surface. */
  /* aria-disabled is included alongside :disabled. DisabledHint consumers use
     aria-disabled (never the native disabled attribute, which makes an element
     unfocusable and would break the hint's keyboard/focus path, see
     ui/DisabledHint). */
  &:disabled,
  &[aria-disabled='true'] {
    cursor: not-allowed;
    background: ${({ theme }) => theme.color.surfaceRaised};
    color: ${({ theme }) => theme.color.textMuted};
    border-color: ${({ theme }) => theme.color.glassBorder};
    box-shadow: none;
  }
`;

export function Button({
  variant = 'primary',
  size = 'md',
  type = 'button',
  ...rest
}: ButtonProps) {
  return <StyledButton $variant={variant} $size={size} type={type} {...rest} />;
}
